import {useQuery} from "@tanstack/react-query";
import {ApiConfig} from "@/config/api.config";
import {
    DashboardAdrResponseDto,
    DashboardIncomeResponseDto,
    DashboardOccupancyRateResponseDto
} from '@/features/dashboard/types/dashboard-dto';
import {UrlHelper} from '@/utils/UrlHelper';
import {DateTimeHelper} from "@/utils/dateTimeHelper.ts";
import {useDashboardFilter} from '@/features/dashboard/hooks/useDashboardFilter';
import {useAxiosAuth} from "@/hooks/useAxiosAuth.ts";
import {useCurrencyStore} from "@/store/useCurrencyStore.ts";

export const useDashboardMainApi = () => {
    const axiosInstance = useAxiosAuth();

    const {filter} = useDashboardFilter();
    const {startDate, endDate, apartmentId} = filter;
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);

    const {currentCurrency} = useCurrencyStore();

    const {
        data: incomeData,
        isLoading: isLoadingIncomeData,
        isError: isErrorIncomeData
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.dashboard.queryKeys.income,
            startDate, endDate, apartmentId, currentCurrency
        ],
        queryFn: async () => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.dashboard.endpoints.income, {
                startDate: DateTimeHelper.getDateOnly(startDate),
                endDate: DateTimeHelper.getDateOnly(endDate),
                apartmentId: apartmentIds
            });
            const {data} = await axiosInstance.get<DashboardIncomeResponseDto>(url);
            return data;
        },
    });

    const {
        data: ADRData,
        isLoading: isLoadingADRData,
        isError: isErrorADRData
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.dashboard.queryKeys.adr,
            startDate, endDate, apartmentId, currentCurrency
        ],
        queryFn: async () => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.dashboard.endpoints.adr, {
                startDate: DateTimeHelper.getDateOnly(startDate),
                endDate: DateTimeHelper.getDateOnly(endDate),
                apartmentId: apartmentIds
            });
            const {data} = await axiosInstance.get<DashboardAdrResponseDto>(url);
            return data;
        },
    });

    const {
        data: occupancyRateData,
        isLoading: isLoadingOccupancyRateData,
        isError: isErrorOccupancyRateData
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.dashboard.queryKeys.occupancyRate,
            startDate, endDate, apartmentId, currentCurrency
        ],
        queryFn: async () => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.dashboard.endpoints.occupancyRate, {
                startDate: DateTimeHelper.getDateOnly(startDate),
                endDate: DateTimeHelper.getDateOnly(endDate),
                apartmentId: apartmentIds
            });
            const {data} = await axiosInstance.get<DashboardOccupancyRateResponseDto>(url);
            return data;
        },
    });

    return {
        incomeData,
        isLoadingIncomeData,
        isErrorIncomeData,
        ADRData,
        isLoadingADRData,
        isErrorADRData,
        occupancyRateData,
        isLoadingOccupancyRateData,
        isErrorOccupancyRateData,
    }
};